import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { axios } from 'services/axios';
import {
    TRUNK_USAGE_VALUES,
    FIELDS_CHANGE_NEED_VALIDATION,
    FIELDS_CHANGE_NO_NEED_VALIDATION,
    TEMPLATE_FILENAME,
    CSV_TYPE,
    CSV_TYPE_ERROR,
    NUMBERS_DNE_ERROR,
    NUMBER_NOT_OK_ERROR,
    NO_DATA_FOUND_ERROR,
    INVALID_CP_ERROR,
    INVALID_TNU_ERROR,
    TRUNKS_DNE_ERROR,
    ADDRESS_DNE_ERROR,
    OUTBOUND_DNE_ERROR,
    DEFAULT_VERIFICATION_RESULT,
    IGNORED_MESSAGE,
    SUCCESS_MESSAGE,
    haveErrorsFilter,
    INVALID_FILENAME,
    SHARED_FIELDS_TO_EXPORT,
    ADDRESS_WRONG_ERROR,
    getVerificationChecks,
    getInstructionsForPlatform,
    INVALID_USER_PLAN_ERROR,
    INVALID_PREFIX_ERROR,
    INVALID_NUMBER_ALLOCATED_ERROR,
    INVALID_PLAN_PERMISSIONS_ERROR,
    INVALID_TRS_REGISTRATION_ERROR,
    INVALID_TRS_REGISTRAR_ERROR,
    INVALID_TRS_NUMBER_UNAVAILABLE_ERROR,
    INVALID_TRS_INCOMPLETE_ERROR,
    TEAMS_REQUIRED_HEADERS,
    GCCH_REQUIRED_HEADERS,
    PRIORITY_REQUIRED_HEADERS,
    NUMBERS_REQUIRED_HEADERS,
    RING_CENTRAL_REQUIRED_HEADERS,
    MISSING_HEADERS_ERROR,
    NO_DATA_ROWS_ERROR,
    buildCompanyFilename,
    makeDateStr,
} from './constants';
import Papa from 'papaparse';
import { isEmpty, startsWith } from 'lodash';
import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import partition from 'lodash/partition';
import concat from 'lodash/concat';
import delay from 'lodash/delay';
import differenceBy from 'lodash/differenceBy';
import orderBy from 'lodash/orderBy';

import { downloadCSV } from 'utils/utils';
import useAddress from 'hooks/useAddress';
import { CSV_TYPE_FX } from 'pages/accounts/users/csv/useCSVUsers';
import appConfig from '../../config.json';
import Cookies from 'js-cookie';
import { ContactSupport } from '@material-ui/icons';

/**
 * Maps platform-specific BE field keys to their CSV export column headers.
 * Shared by the template download and the validation/results exports so both
 * always show the columns relevant to the selected platform.
 */
const getPlatformFieldsToExport = (platformId) => {
    switch (platformId) {
        case 'numbers':
            return {
                id: 'Number ID',
                telephoneNumber: 'Telephone Number',
                cliPresentation: 'Outbound Caller ID',
                plan: 'Plan',
                address: 'Address',
                attribute1: 'Attribute 1',
                attribute2: 'Attribute 2',
                attribute3: 'Attribute 3',
                trsRegistration: 'TRS Registration',
                registrarName: 'Registrar Name',
            };
        case 'priority':
            return {
                telephoneNumber: 'Telephone Number',
            };
        case 'fac64979-385b-42ce-8b1c-d30e87822304' ||
            '6bf86774-7374-4bc3-a113-0d179f3b1488':
            return {
                numberID: 'Number ID',
                telephoneNumber: 'Telephone Number',
                trunk: 'Trunk',
                operatorConnectCallingProfile:
                    'Operator Connect Calling Profile',
                operatorConnectUsageID:
                    'Operator Connect Telephone Number Usage',
                overstampNumber: 'Overstamp Number',
            };
        case '46140cdd-5134-4088-b474-9aad13987c9f':
            return {
                numberID: 'Number ID',
                telephoneNumber: 'Telephone Number',
                trunk: 'Trunk',
                overstampNumber: 'Overstamp Number',
                trunkType: 'type',
            };
        case '86394d2a-acdd-4923-8623-dcae7541e641': // 8x8
            return {
                numberID: 'Number ID',
                telephoneNumber: 'Telephone Number',
                trunk: 'Trunk',
                overstampNumber: 'Overstamp Number',
            };
        case '5873b368-64b4-4262-829a-a626cef2f4f6':
            return {
                numberID: 'Number ID',
                trunk: 'Trunk',
                telephoneNumber: 'Telephone Number',
                overstampNumber: 'Overstamp Number',
            };
        default:
            return {
                numberID: 'Number ID',
                telephoneNumber: 'Telephone Number',
                trunk: 'Trunk',
                operatorConnectCallingProfile:
                    'Operator Connect Calling Profile',
                operatorConnectUsageID:
                    'Operator Connect Telephone Number Usage',
                overstampNumber: 'Overstamp Number',
            }; // Default to Teams
    }
};

export default function useCSVNumbersUpdate() {
    const { currentCompany: companyID, currentCompanyData } = useSelector(
        (state) => state.navigationLists,
    );
    const { requiredScope } = useSelector((state) => state.login);
    const [, setData] = useState([]);
    const [uploadData, setUploadData] = useState(null);
    const [jsonData, setJSONData] = useState(null);
    const [error, setError] = useState(null);
    const [csvLimit, setCSVLimit] = useState(false);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [lookup, setLookup] = useState({
        currentNumbersLookup: {},
        addressesLookup: {},
        addressesByIdLookup: {},
        presentableNumbersLookup: {},
        trunksLookup: {},
        availablePlansLookup: {},
        trsRegistrationsLookup: {},
    });

    const [addressDescription, setAddressDescription] = useState('');
    const [, setAddresses] = useState([]);
    const [, setAddressesId] = useState([]);

    const { concatAddressFields } = useAddress();

    /**
     * Rebuilds the exact "Address" CSV text for a given address record, using
     * the same format used when the template is generated (see
     * handleDownloadCSVTemplate). Used to detect an unchanged address by
     * comparing text-to-text instead of reverse-parsing the CSV's address
     * text, which is unreliable — the concatenated fields it contains (e.g.
     * unit ranges like "6A - 8C") can themselves contain the same ' - '
     * separator used to split off the description, and some addresses have
     * no description at all.
     */
    const buildAddressText = (addressData) => {
        if (!addressData) return '';
        return addressData.description
            ? `${addressData.description} - ${concatAddressFields(addressData)}`
            : concatAddressFields(addressData);
    };

    const [payloads, setPayloads] = useState(null);

    const [verificationChecks, setVerificationChecks] = useState(() => {
        const platform = Cookies.get('selectedPlatformName');
        return getVerificationChecks(platform);
    });
    const [verificationResult, setVerificationResult] = useState(
        DEFAULT_VERIFICATION_RESULT,
    );
    /**
     * for UI Validation progress checks, sets the next index to be in progress.
     * @param {*} i index in VERIFICATION_CHECKS that needs to be updated.
     */
    const changeVerificationChecks = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    newVerificationChecks[index].check = true;
                    if (index < newVerificationChecks.length - 1) {
                        newVerificationChecks[index + 1].current = true;
                    }
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };

    const exportVerificationData = (data, fieldsMap, type, phase) => {
        const exportData = data.map((number) => {
            return Object.keys(fieldsMap).reduce((acc, key) => {
                const value = number[key];
                const newKey = fieldsMap[key];
                acc[newKey] = value ?? '';
                return acc;
            }, {});
        });
        const res = Papa.unparse(exportData);
        const filename = buildCompanyFilename(
            currentCompanyData?.name,
            Cookies.get('selectedPlatformName'),
            type,
            phase,
        );
        downloadCSV(res, filename);
    };

    /**
     * exports data that didnt pass validations (Ignored Records).
     */
    const handleExportInvalidData = () => {
        const fieldsMap = {
            ...getPlatformFieldsToExport(Cookies.get('selectedPlatformId')),
            errors: 'Errors',
        };
        exportVerificationData(
            verificationResult.failed.data,
            fieldsMap,
            'Ignored',
            'Validation',
        );
    };

    /**
     * exports records that passed validation as modifications.
     */
    const handleExportModifiedData = () => {
        const modified = verificationResult.passed.data.filter(
            (v) => v.verificationStatus === SUCCESS_MESSAGE,
        );
        const fieldsMap = getPlatformFieldsToExport(
            Cookies.get('selectedPlatformId'),
        );
        exportVerificationData(modified, fieldsMap, 'Modified', 'Validation');
    };

    /**
     * exports records that were unchanged.
     */
    const handleExportUnchangedData = () => {
        const unchanged = verificationResult.passed.data.filter(
            (v) => v.verificationStatus === IGNORED_MESSAGE,
        );
        const fieldsMap = {
            ...getPlatformFieldsToExport(Cookies.get('selectedPlatformId')),
            errors: 'Errors',
        };
        exportVerificationData(unchanged, fieldsMap, 'Unchanged', 'Validation');
    };

    /**
     * for UI Validation progress checks, increments current index's completionCounter.
     * @param {*} i index in VERIFICATION_CHECKS that needs to be updated.
     */
    const changeVerificationProgresses = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    newVerificationChecks[index].completionCounter += 1;
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };

    // The CSV template never writes the literal "Company Default Plan" text
    // for a number that's on the default plan — it leaves the Plan cell
    // blank (see transformJSONData). But a user can also type that exact
    // text into the CSV to explicitly request the default plan. Both must
    // be treated as equivalent to a null/undefined plan on the original
    // record, or a genuinely unchanged default-plan number would look
    // "changed" on every upload.
    const normalizePlanValue = (value) =>
        value == null || value === '' || value === 'Company Default Plan'
            ? ''
            : value;

    // CSV cells always parse to '' when blank — they can never be null,
    // undefined, or simply absent. The original API record, however,
    // commonly either stores an unset optional field (attributes, overstamp
    // number, etc.) as null, OR omits the key entirely (plan is frequently
    // just never present on the object at all when no custom plan is set).
    // Comparing those directly would flag every number with an unset field
    // as changed — not just because the value differs, but because
    // lodash.isEqual treats two objects with different key sets as unequal
    // even when the "extra" key's value is empty. So both sides are
    // normalized over the union of keys either one carries, not just each
    // object's own keys independently.
    const normalizeValueForCompare = (key, value) =>
        key === 'plan'
            ? normalizePlanValue(value)
            : value === null || value === undefined
              ? ''
              : value;

    const dataIsUnchangedFilter = (originalData, uploadedData) => {
        const allKeys = new Set([
            ...Object.keys(originalData),
            ...Object.keys(uploadedData),
        ]);
        const normalize = (obj) =>
            Array.from(allKeys).reduce((acc, key) => {
                acc[key] = normalizeValueForCompare(key, obj[key]);
                return acc;
            }, {});
        return isEqual(normalize(originalData), normalize(uploadedData));
    };

    /**
     * transforms JSON Data from BE fields into more human readable CSV heading columns
     * @param {*} data
     * @returns
     */
    const sanitizeCSVField = (value) => {
        if (typeof value !== 'string' || value.length === 0) return value;
        const firstChar = value.charAt(0);
        if (
            firstChar === '=' ||
            firstChar === '+' ||
            firstChar === '-' ||
            firstChar === '@' ||
            firstChar === '\t' ||
            firstChar === '\r' ||
            firstChar === '\n' ||
            firstChar === String.fromCharCode(9) || // tab
            firstChar === String.fromCharCode(10) || // newline (LF)
            firstChar === String.fromCharCode(13) // carriage return (CR)
        ) {
            return "'" + value;
        }
        return value;
    };

    const transformJSONData = (data, availablePlatforms = null) => {
        const platformId = Cookies.get('selectedPlatformId');
        const sharedFieldsToExport = getPlatformFieldsToExport(platformId);

        return data.map((number) => {
            const transformedNumber = Object.keys(sharedFieldsToExport).reduce(
                (acc, key) => {
                    let value = number[key];
                    const newKey = sharedFieldsToExport[key];

                    // Ensure telephone number fields are strings and clean non-digits
                    if (
                        (key === 'telephoneNumber' ||
                            newKey === 'Telephone Number') &&
                        value !== undefined &&
                        value !== null
                    ) {
                        value = String(value).replace(/[^\d]/g, '');
                    }

                    // Handle undefined or null values - set to empty string
                    if (value === undefined || value === null) {
                        value = '';
                    }

                    // Sanitize value for CSV injection
                    value = sanitizeCSVField(value);

                    acc[newKey] = value;
                    return acc;
                },
                {},
            );

            // For priority mode, add dynamic platform columns
            if (platformId === 'priority' && availablePlatforms) {
                availablePlatforms.forEach((platform) => {
                    // Use the platform name as defined in TCAP
                    const columnName = platform.name;
                    // Find if this number has this platform and get its priority
                    const platformPriority =
                        number.platformPriorities?.[platform.id] || '';
                    transformedNumber[columnName] = platformPriority;
                });
            }

            return transformedNumber;
        });
    };

    const handleGetCSVData = async () => {
        try {
            setTemplateLoading(true);
            let response;
            const platformId = Cookies.get('selectedPlatformId');
            if (platformId === 'priority') {
                const availablePlatformsResponse = await axios.get(
                    `/Company/${companyID}/Platforms/Available`,
                );
                // Filter unique platforms based on id, excluding 'numbers' and 'priority'
                const platforms = availablePlatformsResponse.data.reduce(
                    (unique, platform) => {
                        const isDuplicate = unique.some(
                            (item) => item.id === platform.id,
                        );
                        if (
                            !isDuplicate &&
                            platform.id !== 'numbers' &&
                            platform.id !== 'priority'
                        ) {
                            unique.push(platform);
                        }
                        return unique;
                    },
                    [],
                );

                const promises = platforms.map((platform) =>
                    axios.get(
                        `/company/${companyID}/PlatformNumbers/${platform.id}`,
                    ),
                );

                const responses = await Promise.all(promises);

                // Build a map of telephone numbers with their platform priorities
                const allData = responses.reduce((acc, res, index) => {
                    const platformData = res.data.platformNumbers;
                    const platform = platforms[index];

                    platformData.forEach((number) => {
                        const key = number.telephoneNumber;
                        if (!acc.has(key)) {
                            acc.set(key, {
                                telephoneNumber: key,
                                numberID: number.numberID,
                                platformPriorities: {}, // Store platform ID -> priority mapping
                            });
                        }

                        const entry = acc.get(key);
                        // Store the priority for this platform using platform ID
                        // Only store if priority has a value
                        if (
                            number.priority !== '' &&
                            number.priority !== null &&
                            number.priority !== undefined
                        ) {
                            entry.platformPriorities[platform.id] =
                                number.priority;
                        }
                    });
                    return acc;
                }, new Map());

                // Transform to final format
                response = {
                    data: Array.from(allData.values()),
                };

                setData(response.data);
                setJSONData(transformJSONData(response.data, platforms));
                return;
            } else if (platformId !== 'numbers') {
                response = await axios.get(
                    `/company/${companyID}/PlatformNumbers/${platformId}`,
                );
            } else {
                response = await axios.get(
                    `/company/${companyID}/numbers/summary`,
                );
                console.log('response', response);
            }

            if (!response.data) {
                // Check if response.data exists
                throw new Error('No numbers data found.');
            }

            if (platformId === 'numbers') {
                // Check the platformId, not platform name
                const addressesResult = await axios.get(
                    `/addresses/${companyID}`,
                );
                const addressesLookup = keyBy(addressesResult.data, 'id');

                response.data.numbers.forEach((number) => {
                    if (number.addressID && addressesLookup[number.addressID]) {
                        const addressData = addressesLookup[number.addressID];
                        number.address = addressData.description
                            ? `${addressData.description} - ${concatAddressFields(addressData)}`
                            : concatAddressFields(addressData);
                    } else {
                        number.address = ''; // Or handle cases where addressID is missing or not found
                    }
                    if (number?.planID) {
                        number.plan = number.planName || '';
                    }
                });
            }
            // Fetch TRS registration data only for numbers platform
            if (platformId === 'numbers') {
                try {
                    // Get all TRS registrars for the company
                    const trsRegistrarsResult = await axios.get(
                        `/TRSRegistrars/${companyID}`,
                    );
                    const trsRegistrarsLookup = keyBy(
                        trsRegistrarsResult.data,
                        'id',
                    );

                    // Get all registrations for each registrar
                    const allRegistrationsPromises =
                        trsRegistrarsResult.data.map((registrar) =>
                            axios
                                .get(`/TRSRegistrations/${registrar.id}`)
                                .then((res) => ({
                                    registrarID: registrar.id,
                                    registrations: res.data,
                                }))
                                .catch(() => ({
                                    registrarID: registrar.id,
                                    registrations: [],
                                })),
                        );

                    const allRegistrations = await Promise.all(
                        allRegistrationsPromises,
                    );

                    // Build a lookup of registrationID -> registration data with registrar info
                    const trsRegistrationsLookup = {};
                    allRegistrations.forEach(
                        ({ registrarID, registrations }) => {
                            registrations.forEach((registration) => {
                                trsRegistrationsLookup[registration.id] = {
                                    ...registration,
                                    registrarID: registrarID,
                                    registrarName:
                                        trsRegistrarsLookup[registrarID]
                                            ?.name || '',
                                };
                            });
                        },
                    );

                    // Add TRS registration info to numbers
                    const numbersArray =
                        platformId === 'numbers'
                            ? response.data.numbers
                            : response.data.platformNumbers;
                    numbersArray.forEach((number) => {
                        if (
                            number.registrationID &&
                            trsRegistrationsLookup[number.registrationID]
                        ) {
                            const registration =
                                trsRegistrationsLookup[number.registrationID];
                            number.trsRegistration =
                                registration.extension || '';
                            number.registrarName =
                                registration.registrarName || '';
                        } else {
                            number.trsRegistration = '';
                            number.registrarName = '';
                        }
                    });
                } catch (e) {
                    console.error('Error fetching TRS registration data:', e);
                    // Continue without TRS data if there's an error
                }
            }

            if (platformId === 'numbers') {
                setData(response.data.numbers);
                setJSONData(transformJSONData(response.data.numbers));
            }
            if (platformId !== 'numbers' && platformId !== 'priority') {
                setData(response.data.platformNumbers);
                setJSONData(transformJSONData(response.data.platformNumbers));
            }
        } catch (e) {
            setError(e);
            console.error('error', { e });
            setTemplateLoading(false);
        }
    };

    /** @note this function invokes and call API when uploading csv */
    // const handleFetchLookupData = async () => {
    //     try {
    //         let allNumbersResult;
    //         const platformId = Cookies.get('selectedPlatformId');

    //         if (platformId !== 'numbers' && platformId!=='priority') {
    //             // Use platform-specific endpoint, platformId is from cookie.
    //             allNumbersResult = await axios.get(`/company/${companyID}/PlatformNumbers/${platformId}`);
    //             allNumbersResult.data.forEach(number => {
    //                 number.priority
    //             });

    //         } else {
    //             // Use original numbers endpoint
    //             allNumbersResult = await axios.get(`/company/${companyID}/numbers`);
    //         }

    //         if (!allNumbersResult.data) {
    //             // Check if response.data exists
    //             throw new Error("No numbers data found.");
    //         }

    //         changeVerificationProgresses(1);

    //         let newLookup = {
    //             currentNumbersLookup: keyBy(allNumbersResult.data, 'numberID'),
    //             presentableNumbersLookup: {},
    //             trunksLookup: {},
    //             addressesLookup: {}, // Initialize addressesLookup
    //         };

    //         if (platformId === 'numbers') {
    //             const addressesResult = await axios.get(`/addresses/${companyID}`);

    //             const addresses = addressesResult.data.map((v) => v.description);
    //             const newAddressById = addressesResult.data.map((item) => ({
    //                 id: item.id,
    //                 description: item.description,
    //             }));

    //             setAddresses(addresses);
    //             setAddressesId(newAddressById);
    //             setAddressDescription(addressesResult?.data[0]?.description); // default address

    //             newLookup.addressesLookup = keyBy(addressesResult.data, 'id'); // Add addresses to lookup
    //         }

    //         changeVerificationProgresses(1);

    //         const presentableNumbersResult = await axios.get(
    //             `/company/${companyID}/numbers/presentable`,
    //         );

    //         changeVerificationProgresses(1);

    //         const trunksResult = await axios.get(`/trunks/${companyID}`);

    //         newLookup.presentableNumbersLookup = keyBy(
    //             presentableNumbersResult.data,
    //             'telephoneNumber',
    //         );
    //         newLookup.trunksLookup = keyBy(trunksResult.data, 'sipHeader');

    //         newLookup.presentableNumbersLookup['Restricted'] = 'Restricted';
    //         if(platformId==='priority'){
    //             newLookup={
    //                 presentableNumbersLookup:
    //                    allNumbersResult.data,
    //             }
    //         }
    //         console.log(newLookup)
    //         changeVerificationProgresses(1);
    //         changeVerificationChecks(1);
    //         setLookup(newLookup);
    //     } catch (e) {
    //         setError(e);
    //         console.error(e);
    //     }
    // };
    const handleFetchLookupData = async () => {
        try {
            let allNumbersResult;
            const platformId = Cookies.get('selectedPlatformId');
            const platformName = Cookies.get('selectedPlatformName');

            if (platformId !== 'numbers' && platformId !== 'priority') {
                allNumbersResult = await axios.get(
                    `/company/${companyID}/PlatformNumbers/${platformId}`,
                );
                allNumbersResult.data.platformNumbers.forEach((number) => {
                    number.priority;
                });
            } else {
                allNumbersResult = await axios.get(
                    `/company/${companyID}/numbers/summary`,
                );
                if (platformId === 'numbers') {
                    // Mirror handleDownloadCSVTemplate's enrichment so the
                    // validation lookup's numbers have the same resolved
                    // `.plan` field the CSV shows — without this, any number
                    // with a real (non-default) plan assigned always compares
                    // as changed, since `.plan` is otherwise never set here.
                    allNumbersResult.data.numbers.forEach((number) => {
                        if (number?.planID) {
                            number.plan = number.planName || '';
                        }
                    });
                }
            }

            changeVerificationProgresses(1);
            const addressesResult = await axios.get(`/addresses/${companyID}`);

            const addresses = addressesResult.data.map((v) => v.description);
            const newAddressById = addressesResult.data.map((item) => ({
                id: item.id,
                description: item.description,
            }));
            setAddresses(addresses);
            setAddressesId(newAddressById);
            setAddressDescription(addressesResult?.data[0]?.description);
            changeVerificationProgresses(1);
            const presentableNumbersResult = await axios.get(
                `/company/${companyID}/numbers/presentable`,
            );
            changeVerificationProgresses(1);
            const trunksResult = await axios.get(`/trunks/${companyID}`);
            var availablePlansResponse = [];
            // if (currentCompanyData?.companyBillingSettings?.perNumberPlan != 0) {
            // don't use current company data plans (SPA data is lost)
            availablePlansResponse = (
                await axios.get(`/company/${companyID}/plans`)
            ).data;
            //

            let idKey = platformName === 'Numbers' ? 'id' : 'numberID';

            let newLookup = {
                // Change const to let here
                currentNumbersLookup: keyBy(
                    platformId === 'numbers'
                        ? allNumbersResult.data.numbers
                        : allNumbersResult.data.platformNumbers,
                    idKey,
                ),
                addressesLookup: keyBy(addressesResult.data, 'description'),
                addressesByIdLookup: keyBy(addressesResult.data, 'id'),
                presentableNumbersLookup: keyBy(
                    presentableNumbersResult.data,
                    'telephoneNumber',
                ),
                trunksLookup: keyBy(trunksResult.data, 'sipHeader'),
                availablePlansLookup: keyBy(availablePlansResponse, 'name'),
                trsRegistrationsLookup: {},
            };

            // Fetch TRS registrations lookup only for numbers platform
            if (platformId === 'numbers') {
                try {
                    const trsRegistrarsResult = await axios.get(
                        `/TRSRegistrars/${companyID}`,
                    );
                    const trsRegistrarsLookup = keyBy(
                        trsRegistrarsResult.data,
                        'id',
                    );

                    const allRegistrationsPromises =
                        trsRegistrarsResult.data.map((registrar) =>
                            axios
                                .get(`/TRSRegistrations/${registrar.id}`)
                                .then((res) => ({
                                    registrarID: registrar.id,
                                    registrations: res.data,
                                }))
                                .catch(() => ({
                                    registrarID: registrar.id,
                                    registrations: [],
                                })),
                        );

                    const allRegistrations = await Promise.all(
                        allRegistrationsPromises,
                    );

                    // Build a lookup of registrationID -> registration data with registrar info
                    const trsRegistrationsLookup = {};
                    allRegistrations.forEach(
                        ({ registrarID, registrations }) => {
                            registrations.forEach((registration) => {
                                trsRegistrationsLookup[registration.id] = {
                                    ...registration,
                                    registrarID: registrarID,
                                    registrarName:
                                        trsRegistrarsLookup[registrarID]
                                            ?.name || '',
                                };
                            });
                        },
                    );

                    newLookup.trsRegistrationsLookup = trsRegistrationsLookup;
                } catch (e) {
                    console.error(
                        'Error fetching TRS registrations lookup:',
                        e,
                    );
                    // Continue without TRS data if there's an error
                }
            }

            newLookup.presentableNumbersLookup['Restricted'] = 'Restricted';
            if (platformId === 'priority') {
                newLookup = {
                    presentableNumbersLookup: allNumbersResult.data.numbers,
                };
            }

            changeVerificationProgresses(1);
            changeVerificationChecks(1);
            setLookup(newLookup);
        } catch (e) {
            setError(e);
            console.error(e);
        }
    };
    /**
     * converts the template from JSON into CSV
     */
    const processTemplate = () => {
        // jsonData should now contain telephone numbers as strings from transformJSONData
        const processedData = jsonData.map((item) => ({ ...item }));

        const unparseConfig = {
            quotes: false,
            quoteChar: '"',
            escapeChar: '"',
            delimiter: ',',
            header: true,
        };

        const res = Papa.unparse(processedData, unparseConfig);
        const platformName = Cookies.get('selectedPlatformName') || 'numbers';
        const platformId = Cookies.get('selectedPlatformId') || 'numbers';

        // Matches the Users CSV template naming convention:
        // Company_Type_timestamp.csv
        const slugify = (str) =>
            (str || '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
        const companySlug = slugify(currentCompanyData?.name) || 'Company';
        const type =
            platformName.toLowerCase() === 'priority' ||
            platformId === 'priority'
                ? 'Priority'
                : platformId === 'numbers' ||
                    platformName.toLowerCase() === 'numbers'
                  ? 'Numbers'
                  : platformName;
        const templateFileName = `${companySlug}_${slugify(type) || 'Numbers'}_${makeDateStr()}.csv`;

        downloadCSV(res, templateFileName);
        setTemplateLoading(false);
    };

    const handleDownloadCSVTemplate = async () => {
        await handleGetCSVData();
    };

    //handle Download CSV template
    React.useEffect(() => {
        if (jsonData) {
            processTemplate();
        }
    }, [jsonData]);

    /**
     * use papaparse to convert CSV -> JSON (with webworker).
     * @param {*} data CSV data to be converted to JSON.
     * @returns JSON data
     */
    const convertCSVDataToJSON = async (data) => {
        return new Promise((resolve) => {
            const papaparseConfig = {
                dynamicTyping: false, // Crucial: Keep everything as strings
                header: true,
                worker: true, // Keep using worker
                skipEmptyLines: true,
                // transformHeader: (header) => header.trim(), // <-- REMOVE THIS LINE for worker compatibility
                complete: function (results) {
                    // Manually trim headers after parsing if needed (though PapaParse usually handles basic whitespace)
                    // If headers still have issues, you might need to process results.meta.fields here
                    // For example: const trimmedHeaders = results.meta.fields.map(h => h.trim());

                    const sanitizeCSVField = (value) => {
                        if (typeof value !== 'string' || value.length === 0)
                            return value;
                        const firstChar = value.charAt(0);
                        // Check for =, +, -, @, tab, carriage return, and newline
                        if (
                            firstChar === '=' ||
                            firstChar === '+' ||
                            firstChar === '-' ||
                            firstChar === '@' ||
                            firstChar === '\t' ||
                            firstChar === '\r' ||
                            firstChar === '\n' ||
                            firstChar === String.fromCharCode(9) || // tab
                            firstChar === String.fromCharCode(10) || // newline (LF)
                            firstChar === String.fromCharCode(13) // carriage return (CR)
                        ) {
                            return "'" + value;
                        }
                        return value;
                    };
                    const processedData = results.data.map((row) => {
                        const newRow = { ...row };
                        Object.keys(newRow).forEach((key) => {
                            if (typeof newRow[key] === 'string') {
                                newRow[key] = sanitizeCSVField(newRow[key]);
                            }
                        });
                        // Access headers safely, assuming PapaParse trimmed them correctly or handle potential whitespace
                        const telephoneHeader = 'Telephone Number'; // Use the expected final header name

                        // Handle telephone number fields
                        if (
                            newRow[telephoneHeader] !== undefined &&
                            newRow[telephoneHeader] !== null
                        ) {
                            // Remove potential ="..." formatting and keep only digits
                            let telNum = String(newRow[telephoneHeader]).trim();
                            if (
                                telNum.startsWith('="') &&
                                telNum.endsWith('"')
                            ) {
                                telNum = telNum.substring(2, telNum.length - 1);
                            }
                            newRow[telephoneHeader] = telNum.replace(
                                /[^\d]/g,
                                '',
                            ); // Ensure clean digits string
                        }
                        // Add similar handling if 'telephoneNumber' could be a header from CSV
                        // const otherTelephoneHeader = 'telephoneNumber';
                        // if (newRow[otherTelephoneHeader] !== undefined && newRow[otherTelephoneHeader] !== null) {
                        //     let telNum = String(newRow[otherTelephoneHeader]).trim();
                        //     if (telNum.startsWith('="') && telNum.endsWith('"')) {
                        //         telNum = telNum.substring(2, telNum.length - 1);
                        //     }
                        //     newRow[otherTelephoneHeader] = telNum.replace(/[^\d]/g, '');
                        // }
                        return newRow;
                    });

                    results.data = processedData;

                    return resolve(results);
                },
            };
            Papa.parse(data, papaparseConfig);
        });
    };

    /**
     * aggregates data with 0 errors with unlicensedUsersChanged into allSucceedData,
     * aggregates all data with errors & sort them by UPN,
     * aggregates all ignored users with allSucceedData & sort them by (verificationStatus, UPN)
     * @param {data, unlicensedUsersChanged, ignored} param0
     * @returns {allSucceedData}
     */
    const reportResult = ({
        noNeedValidationWithErrors,
        needValidationWithErrors,
        unchanged,
    }) => {
        const failed = concat(
            noNeedValidationWithErrors.filter(haveErrorsFilter),
            needValidationWithErrors.filter(haveErrorsFilter),
        );
        const result =
            Cookies.get('selectedPlatformName') === 'Numbers'
                ? 'id'
                : 'numberID';
        const success = differenceBy(
            concat(noNeedValidationWithErrors, needValidationWithErrors),
            failed,
            result,
        );
        setVerificationResult((prev) => {
            const newVerificationResult = { ...prev };
            newVerificationResult.passed.data = orderBy(
                concat(unchanged, success).map((v) => ({
                    ...v,
                    verificationStatus: v.ignored
                        ? IGNORED_MESSAGE
                        : SUCCESS_MESSAGE,
                })),
                ['verificationStatus', 'telephoneNumber'],
                ['desc', 'asc'],
            );
            newVerificationResult.failed.data = orderBy(
                failed,
                ['telephoneNumber'],
                ['asc'],
            );
            return newVerificationResult;
        });
        return { allSucceedData: success };
    };

    // Determine required headers based on platform ID stored in cookie
    const getRequiredHeaders = () => {
        const platformId = Cookies.get('selectedPlatformId');
        const platformName = Cookies.get('selectedPlatformName');
        if (platformName === 'Priority' || platformId === 'priority') {
            return PRIORITY_REQUIRED_HEADERS;
        }
        if (platformId === 'numbers' || platformName === 'Numbers') {
            return NUMBERS_REQUIRED_HEADERS;
        }
        if (platformName === 'RingCentral') {
            return RING_CENTRAL_REQUIRED_HEADERS;
        }
        // GCCH platform IDs
        if (
            platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
            platformId === '5873b368-64b4-4262-829a-a626cef2f4f6'
        ) {
            return GCCH_REQUIRED_HEADERS;
        }
        // Teams platform IDs (or any other OC-bearing platform)
        if (
            platformId === 'fac64979-385b-42ce-8b1c-d30e87822304' ||
            platformId === '6bf86774-7374-4bc3-a113-0d179f3b1488'
        ) {
            return TEAMS_REQUIRED_HEADERS;
        }
        // Other platforms don't yet have a dedicated required-header set, but
        // every CSV must still identify the number being updated.
        return ['Telephone Number'];
    };

    const handleUploadData = async (e) => {
        if (
            e.target.files[0].type === CSV_TYPE ||
            e.target.files[0].type === CSV_TYPE_FX
        ) {
            const data = e.target.files[0];
            const { data: newDataJSON } = await convertCSVDataToJSON(data);
            const platform = Cookies.get('selectedPlatformName');
            const verfChecks = getVerificationChecks(platform);

            // Validate at least one data row
            if (!newDataJSON || newDataJSON.length === 0) {
                setError(NO_DATA_ROWS_ERROR);
                return;
            }

            // Validate required headers are present
            const requiredHeaders = getRequiredHeaders();
            if (requiredHeaders) {
                const presentHeaders = Object.keys(newDataJSON[0] || {});
                const missing = requiredHeaders.filter(
                    (h) => !presentHeaders.includes(h),
                );
                if (missing.length > 0) {
                    setError(MISSING_HEADERS_ERROR(missing));
                    return;
                }
            }
            // check for csv limits
            if (newDataJSON.length > appConfig.csvLimit) {
                setCSVLimit(true);
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    verfChecks.forEach((v, index) => {
                        v.check = false;
                        v.current = index === 0; // NOTE: restart at Received CSV check
                        v.completionCounter = 0;
                    });
                    return newVerificationChecks;
                });
                return;
            }

            if (csvLimit) setCSVLimit(false);

            //save data
            // setLoading(true);
            setVerificationChecks(verfChecks);
            setUploadData(newDataJSON);
            changeVerificationProgresses(0);
            changeVerificationChecks(0);

            //fetch data
            handleFetchLookupData();
        } else {
            window.alert(CSV_TYPE_ERROR);
        }
    };

    const handleMock = () => {
        handleDownloadCSVTemplate();
    };

    //convert CSV columns into friendlier backend's JSON field
    // const convertCSVHeaders = (csv) => {
    //     return csv
    //         .filter((v) => v['Number ID'] || v['id'] || v['Telephone Number'] )
    //         .map((row) => {
    //             let newRow = { errors: [] };
    //             const platformName = Cookies.get('selectedPlatformName');
    //             let fieldsToExport;

    //             if (platformName === 'numbers') {
    //                 fieldsToExport = {
    //                     id: 'Number ID',
    //                     telephoneNumber: 'Telephone Number',
    //                     overstampNumber: 'Overstamp Number',
    //                     address: 'Address',
    //                 };
    //             } else if(platformName === 'Priority'){
    //                 fieldsToExport = {
    //                     telephoneNumber: 'Telephone Number',
    //                 };
    //             }
    //             else if (platformName === 'zoom' || platformName === 'SBCaaS') {
    //                 fieldsToExport = {
    //                     numberID: 'Number ID',
    //                     telephoneNumber: 'Telephone Number',
    //                     overstampNumber: 'Overstamp Number',
    //                     trunk: 'Trunk',
    //                 };
    //             } else {
    //                 fieldsToExport = {
    //                     numberID: 'Number ID',
    //                     telephoneNumber: 'Telephone Number',
    //                     overstampNumber: 'Overstamp Number',
    //                     trunk: 'Trunk',
    //                     operatorConnectCallingProfile: 'Operator Connect Calling Profile',
    //                     operatorConnectUsageID: 'Operator Connect Telephone Number Usage',
    //                 };
    //             }
    //             if (platformName === 'Priority') {
    //                 // Only carry telephoneNumber as it is.
    //                 let telephoneNumber = row['Telephone Number'];
    //                 console.log(telephoneNumber);

    //                 let id = undefined; // Initialize id to undefined
    //                 let extractedNumber = undefined; // Initialize extractedNumber to undefined

    //                 if (typeof telephoneNumber === 'string' && telephoneNumber.includes(',')) {
    //                     extractedNumber = telephoneNumber.split(',')[0]; // Extract number before comma

    //                     if (lookup.presentableNumbersLookup && Array.isArray(lookup.presentableNumbersLookup)) {
    //                         const foundItem = lookup.presentableNumbersLookup.find(item => {
    //                             return item.telephoneNumber === extractedNumber;
    //                         });

    //                         if (foundItem) {
    //                             id = foundItem.id;
    //                         }
    //                     }
    //                 }

    //                 let newRow = { id, telephoneNumber };
    //                 console.log(newRow);
    //                 return newRow;
    //             }
    //             Object.entries(fieldsToExport).forEach(([beField, csvColumn]) => {
    //                 newRow[beField] = row[csvColumn];
    //             });

    //             let originalRow; // declare originalRow

    //             if (platformName === 'numbers') {
    //                 originalRow = lookup.currentNumbersLookup[newRow.id] || {}; // handle undefined
    //             } else {
    //                 originalRow = lookup.currentNumbersLookup[newRow.numberID] || {}; // handle undefined
    //             }

    //             console.log(originalRow);

    //             if (platformName === 'numbers') {
    //                 if (!newRow.address && originalRow.addressID) {
    //                     newRow.addressID = '';
    //                 } else {
    //                     const splitAddress = newRow.address?.split(' - ');
    //                     const description = splitAddress?.[0];

    //                     newRow.addressID = newRow.address
    //                         ? lookup.addressesLookup[description]?.id || 'INVALID'
    //                         : originalRow.addressID;
    //                 }
    //             }
    //             if(platformName!=='numbers'&&platformName!=='Priority'){
    //             newRow.overstampNumber = String(originalRow.overstampNumber || '');
    //             newRow.telephoneNumber = String(originalRow.telephoneNumber || '');
    //             newRow.trunk = originalRow.trunk || '';
    //             newRow.operatorConnectCallingProfile = newRow.operatorConnectCallingProfile || '';
    //             newRow.priority = originalRow.priority;
    //             newRow.platformId = originalRow.platformId;
    //             console.log(newRow)
    //             }
    //             return newRow;
    //         });
    // };
    const convertCSVHeaders = (csv) => {
        return csv
            .filter((v) => v['Number ID'] || v['id'] || v['Telephone Number'])
            .map((row) => {
                let newRow = { errors: [] };
                const platformName = Cookies.get('selectedPlatformId');
                let fieldsToExport;

                if (platformName === 'numbers') {
                    fieldsToExport = {
                        id: 'Number ID',
                        telephoneNumber: 'Telephone Number',
                        overstampNumber: 'Overstamp Number',
                        plan: 'Plan',
                        address: 'Address',
                        attribute1: 'Attribute 1',
                        attribute2: 'Attribute 2',
                        attribute3: 'Attribute 3',
                        cliPresentation: 'Outbound Caller ID',
                        trsRegistration: 'TRS Registration',
                        registrarName: 'Registrar Name',
                    };
                } else if (platformName === 'priority') {
                    fieldsToExport = {
                        telephoneNumber: 'Telephone Number',
                        priority: 'priority',
                    };
                } else if (
                    platformName === '46140cdd-5134-4088-b474-9aad13987c9f' ||
                    platformName === '5873b368-64b4-4262-829a-a626cef2f4f6'
                ) {
                    fieldsToExport = {
                        numberID: 'Number ID',
                        telephoneNumber: 'Telephone Number',
                        overstampNumber: 'Overstamp Number',
                        trunk: 'Trunk',
                    };
                } else if (
                    platformName === '86394d2a-acdd-4923-8623-dcae7541e641'
                ) {
                    // 8x8
                    fieldsToExport = {
                        numberID: 'Number ID',
                        telephoneNumber: 'Telephone Number',
                        overstampNumber: 'Overstamp Number',
                        trunk: 'Trunk',
                    };
                } else {
                    fieldsToExport = {
                        numberID: 'Number ID',
                        telephoneNumber: 'Telephone Number',
                        overstampNumber: 'Overstamp Number',
                        trunk: 'Trunk',
                        operatorConnectCallingProfile:
                            'Operator Connect Calling Profile',
                        operatorConnectUsageID:
                            'Operator Connect Telephone Number Usage',
                    };
                }

                if (platformName === 'priority') {
                    let telephoneNumber = row['Telephone Number'];

                    // Parse platform priorities from CSV columns
                    const platformPriorities = {};

                    // Get all columns from the CSV row
                    Object.keys(row).forEach((columnName) => {
                        // Skip the telephone number column
                        if (
                            columnName !== 'Telephone Number' &&
                            row[columnName] !== '' &&
                            row[columnName] !== null &&
                            row[columnName] !== undefined
                        ) {
                            // This is a platform column - store the priority value
                            // columnName will be the platform name as defined in TCAP (e.g., "Microsoft_Teams", "SBCaaS")
                            platformPriorities[columnName] =
                                parseInt(row[columnName], 10) || 0;
                        }
                    });

                    newRow = {
                        telephoneNumber,
                        platformPriorities, // Store platform name -> priority mapping
                    };
                    return newRow;
                } else {
                    Object.entries(fieldsToExport).forEach(
                        ([beField, csvColumn]) => {
                            newRow[beField] = row[csvColumn];
                        },
                    );

                    let originalRow;

                    if (platformName === 'numbers') {
                        originalRow =
                            lookup.currentNumbersLookup[newRow.id] || {};
                    } else {
                        originalRow =
                            lookup.currentNumbersLookup[newRow.numberID] || {};
                    }

                    if (platformName === 'numbers') {
                        if (!newRow.address && originalRow.addressID) {
                            newRow.addressID = '';
                            newRow.plan = row['Plan'];
                            let isPlanChanged = false;
                            if (
                                currentCompanyData?.companyBillingSettings
                                    ?.perUserPlan != 0
                            ) {
                                // The CSV never writes literal "Company
                                // Default Plan" text for a default-plan
                                // number — it leaves the cell blank. Compare
                                // both sides through the same normalization
                                // used everywhere else so this doesn't read
                                // as "changed" for an untouched CSV.
                                isPlanChanged =
                                    normalizePlanValue(originalRow.plan) !==
                                    normalizePlanValue(row['Plan']);
                            }
                            newRow.isPlanChanged = isPlanChanged;
                        } else {
                            // Compare against the exact text the template
                            // would show for the number's current address
                            // before trying to reverse-parse a new one — the
                            // split-by-' - ' lookup below breaks when the
                            // address has no description, or its own fields
                            // (e.g. a unit range like "6A - 8C") contain the
                            // same separator used to split off the
                            // description.
                            const currentAddressText = buildAddressText(
                                lookup.addressesByIdLookup?.[
                                    originalRow.addressID
                                ],
                            );
                            if (newRow.address === currentAddressText) {
                                newRow.addressID = originalRow.addressID;
                            } else {
                                const splitAddress =
                                    newRow.address?.split(' - ');
                                const description = splitAddress?.[0];
                                newRow.addressID = newRow.address
                                    ? lookup.addressesLookup[description]?.id ||
                                      'INVALID'
                                    : originalRow.addressID;
                            }
                            newRow.plan = row['Plan'];
                            let isPlanChanged = false;
                            if (
                                currentCompanyData?.companyBillingSettings
                                    ?.perNumberPlan != 0 &&
                                row['Plan'] != null &&
                                row['Plan'] !== ''
                            ) {
                                // See the comment on the first branch above —
                                // normalize both sides the same way rather
                                // than assuming the CSV writes literal
                                // "Company Default Plan" text.
                                isPlanChanged =
                                    normalizePlanValue(originalRow.plan) !==
                                    normalizePlanValue(row['Plan']);
                            }
                            newRow.isPlanChanged = isPlanChanged;
                        }
                    }
                    if (
                        platformName !== 'numbers' &&
                        platformName !== 'Priority'
                    ) {
                        newRow.overstampNumber = String(
                            row['Overstamp Number'] || '',
                        );
                        newRow.telephoneNumber = String(
                            row['Telephone Number'] || '',
                        );
                        newRow.trunk = row['Trunk'] || '';
                        newRow.operatorConnectCallingProfile =
                            row['Operator Connect Calling Profile'] || '';
                        newRow.operatorConnectUsageID =
                            row['Operator Connect Telephone Number Usage'] ||
                            '';
                        newRow.priority = originalRow.priority;
                        newRow.platformId = originalRow.platformId;
                    }
                    return newRow;
                }
            });
    };
    const filterChangedData = (data) => {
        const platformName = Cookies.get('selectedPlatformName');
        let fieldsChangeNeedValidation;

        if (platformName === 'Priority') {
            fieldsChangeNeedValidation = [];
        } else if (platformName === 'Numbers') {
            // 'address' is the raw CSV text and has no counterpart on the
            // original API record (only 'addressID' does) — comparing it
            // directly would flag every address-assigned number as changed.
            fieldsChangeNeedValidation = [
                'plan',
                'addressID',
                'cliPresentation',
                'trsRegistration',
                'registrarName',
            ];
        } else {
            // 'operatorConnectCallingProfile' (a name) is what convertCSVHeaders
            // sets on the row — the original API record only has the ID, so it
            // must be resolved to a name below before comparing (see
            // mappedOriginalRow.operatorConnectCallingProfile).
            fieldsChangeNeedValidation = [
                'trunk',
                'operatorConnectCallingProfile',
                'operatorConnectUsageID',
            ];
        }

        const idKey = platformName === 'Numbers' ? 'id' : 'numberID';

        // The Numbers platform's CSV/table has no "Overstamp Number" column
        // (see NUMBERS_REQUIRED_HEADERS / getColumns) — comparing it would
        // always show as changed for any number that has one set, since the
        // CSV can never carry that value for this platform.
        const fieldsChangeNoNeedValidation =
            platformName === 'Numbers'
                ? FIELDS_CHANGE_NO_NEED_VALIDATION.filter(
                      (f) => f !== 'overstampNumber',
                  )
                : FIELDS_CHANGE_NO_NEED_VALIDATION;

        const [unchanged, changed] = partition(data, (row) => {
            const fieldsToCompare = fieldsChangeNeedValidation.concat(
                fieldsChangeNoNeedValidation,
            );
            const pickedRow = pick(row, fieldsToCompare);
            const originalRow = lookup.currentNumbersLookup[row[idKey]];

            // Map original row's TRS data to match the CSV field names
            const mappedOriginalRow = { ...originalRow };
            if (platformName === 'Numbers' && originalRow) {
                // Map registrationID to trsRegistration and registrarName from lookup
                if (
                    originalRow.registrationID &&
                    lookup.trsRegistrationsLookup &&
                    lookup.trsRegistrationsLookup[originalRow.registrationID]
                ) {
                    const trsData =
                        lookup.trsRegistrationsLookup[
                            originalRow.registrationID
                        ];
                    mappedOriginalRow.trsRegistration = trsData.extension || '';
                    mappedOriginalRow.registrarName =
                        trsData.registrarName || '';
                } else {
                    mappedOriginalRow.trsRegistration = '';
                    mappedOriginalRow.registrarName = '';
                }
                // 'plan' equivalence (null/undefined/'' vs the literal
                // "Company Default Plan" text) is handled generically by
                // normalizeForCompare — no per-row sentinel needed here.
            }
            // 'operatorConnectCallingProfile' is already a plain name on the
            // raw API record (see transformJSONData, which reads it the same
            // way for the template download) — no ID resolution needed. An
            // earlier attempt to resolve it via the trunk's callingProfileList
            // was wrong and always forced it to '', making every number with
            // a real calling profile compare as changed.

            const pickedOriginalRow = pick(mappedOriginalRow, fieldsToCompare);
            return dataIsUnchangedFilter(pickedRow, pickedOriginalRow);
        });

        const [noNeedValidation, needValidation] = partition(changed, (row) => {
            const pickedRow = pick(row, fieldsChangeNeedValidation);
            const originalRow = lookup.currentNumbersLookup[row[idKey]];

            // Map original row's TRS data to match the CSV field names
            const mappedOriginalRow = { ...originalRow };
            if (platformName === 'Numbers' && originalRow) {
                if (
                    originalRow.registrationID &&
                    lookup.trsRegistrationsLookup &&
                    lookup.trsRegistrationsLookup[originalRow.registrationID]
                ) {
                    const trsData =
                        lookup.trsRegistrationsLookup[
                            originalRow.registrationID
                        ];
                    mappedOriginalRow.trsRegistration = trsData.extension || '';
                    mappedOriginalRow.registrarName =
                        trsData.registrarName || '';
                } else {
                    mappedOriginalRow.trsRegistration = '';
                    mappedOriginalRow.registrarName = '';
                }
                // 'plan' equivalence is handled generically by
                // normalizeForCompare — no per-row sentinel needed here.
            }
            // 'operatorConnectCallingProfile' is already a plain name on the
            // raw API record — no ID resolution needed (see the comment on
            // the first partition above).

            const pickedOriginalRow = pick(
                mappedOriginalRow,
                fieldsChangeNeedValidation,
            );
            return dataIsUnchangedFilter(pickedRow, pickedOriginalRow);
        });

        return {
            noNeedValidation,
            needValidation,
            unchanged: unchanged.map((v) => ({
                ...v,
                ignored: true,
            })),
        };
    };

    /**
     *  make sure number is OK and Exists.
     *
     * @param {*} data
     * @returns
     */
    const verifyNumbers = (data) => {
        const platformName = Cookies.get('selectedPlatformName');
        const idKey = platformName === 'Numbers' ? 'id' : 'numberID';
        const [numbersExist, numbersNotExist] = partition(data, (row) => {
            const originNumber = lookup.currentNumbersLookup[row[idKey]];
            return originNumber;
        });

        const [numbersOKAndExist, numbersNotOK] = partition(
            numbersExist,
            (v) => {
                let originNumber;
                if (platformName === 'Numbers') {
                    originNumber =
                        lookup.currentNumbersLookup[v[idKey]].canEdit;
                } else {
                    originNumber = lookup.currentNumbersLookup[v[idKey]];
                }

                return originNumber;
            },
        );
        return {
            numbersNotExist,
            numbersNotOK,
            numbersOKAndExist,
        };
    };

    /**
     * verifies Trunks exists & OC fields are edited properly.
     * TODO: prefill OC fields.
     * @param {*} data
     * @returns
     */
    const verifyPlans = (data, availablePlansLookup) => {
        let invalidUserPlan = [],
            invalidPrefix = [],
            invalidNumberAllocated = [],
            invalidPlanPermissions = [];
        // if (currentCompanyData?.companyBillingSettings?.perUserPlan != 0) {
        data.filter((userData) => {
            // only validate the plan when it was actually changed on this row
            if (!userData.isPlanChanged) {
                return;
            }
            // plan is changed and per user plan allows and telephone number is not empty
            if (
                requiredScope < 40
                // &&
                // !isEmpty(userData.telephoneNumber)
            ) {
                invalidPlanPermissions.push(userData);
                return;
            } else {
                var userPlan = isEqual(userData.plan, 'Company Default Plan')
                    ? availablePlansLookup[currentCompanyData.defaultPlan]
                    : availablePlansLookup[userData.plan];

                if (!isEmpty(userPlan)) {
                    var validNumberAllocated = !isEmpty(
                        userData.telephoneNumber,
                    );
                    if (!validNumberAllocated) {
                        invalidNumberAllocated.push(userData);
                        return;
                    }
                    var validPrefix = startsWith(
                        userData.telephoneNumber,
                        userPlan.countryPrefix,
                    );
                    if (!validPrefix) {
                        invalidPrefix.push(userData);
                        return;
                    }
                } else {
                    if (
                        userData.plan !== '' &&
                        userData.plan !== currentCompanyData.defaultPlan
                    ) {
                        invalidUserPlan.push(userData);
                    }
                }
            }
        });
        // }
        return {
            invalidUserPlan,
            invalidPrefix,
            invalidNumberAllocated,
            invalidPlanPermissions,
        };
    };
    const verifyTrunks = (data) => {
        const [trunksExist, trunksNotExist] = partition(data, (row) => {
            // Treat empty trunk as existing
            return row.trunk === '' || Boolean(lookup.trunksLookup[row.trunk]);
        });
        const withOCTrunks = trunksExist.filter(
            (row) =>
                lookup.trunksLookup[row.trunk]?.trunkType?.name ===
                'MS Operator Connect',
        );
        changeVerificationProgresses(4);
        const ocTrunksHaveInvalidCP = withOCTrunks.filter((number) => {
            const trunk = lookup.trunksLookup[number.trunk];
            const callingProfileList = trunk?.callingProfileList ?? [];
            const cpExists = callingProfileList.find(
                (v) => v.name === number.operatorConnectCallingProfile,
            );
            return !cpExists;
        });
        const ocTrunksHaveInvalidTNU = withOCTrunks.filter(
            (number) =>
                !TRUNK_USAGE_VALUES.includes(number.operatorConnectUsageID),
        );
        changeVerificationProgresses(4);
        changeVerificationChecks(4);
        return {
            ocTrunksHaveInvalidCP,
            ocTrunksHaveInvalidTNU,
            trunksNotExist,
        };
    };

    const verifyEightByEight = (data) => {
        // Trunk empty = disable (valid). Trunk populated = must exist.
        const trunksNotExist = data.filter(
            (row) => row.trunk && !lookup.trunksLookup[row.trunk],
        );
        changeVerificationProgresses(4);
        changeVerificationChecks(4);
        return { trunksNotExist };
    };

    const verifyAddress = (data) => {
        const invalidAddress = data.filter((v) => v.addressID === 'INVALID');
        return {
            invalidAddress,
        };
    };

    const verifyOutboundCallerID = (data) => {
        const invalidOutboundCallerID = data.filter(
            (v) => !lookup.presentableNumbersLookup[v.cliPresentation],
        );
        return {
            invalidOutboundCallerID,
        };
    };

    /**
     * Verifies TRS registration data (only called for numbers where TRS fields have changed):
     * - Both fields empty = pass (will delete existing registration if any)
     * - One field filled and other empty = fail (incomplete)
     * - Both fields filled = validate:
     *   1. Registration must exist under registrar available to company
     *   2. Number must not already be assigned to another number
     * @param {*} data - Only numbers where TRS fields have changed
     * @returns
     */
    const verifyTRSRegistration = async (data) => {
        const invalidTRSIncomplete = [];
        const invalidTRSRegistration = [];
        const invalidTRSRegistrar = [];
        const invalidTRSNumberUnavailable = [];

        // Get platform name for number ID key
        const platformName = Cookies.get('selectedPlatformName');

        // Validate each row where TRS has changed
        for (const row of data) {
            const hasRegistration =
                row.trsRegistration && row.trsRegistration.trim() !== '';
            const hasRegistrar =
                row.registrarName && row.registrarName.trim() !== '';

            // Both empty = pass (will delete existing registration)
            if (!hasRegistration && !hasRegistrar) {
                continue;
            }

            // One filled and other empty = fail (incomplete)
            if (hasRegistration !== hasRegistrar) {
                invalidTRSIncomplete.push(row);
                continue;
            }

            // Both filled = validate
            if (hasRegistration && hasRegistrar) {
                try {
                    const numberID =
                        platformName === 'Numbers' ? row.id : row.numberID;
                    const currentNumber = lookup.currentNumbersLookup[numberID];

                    // Find the registrationID from the lookup based on the name/extension provided
                    let registrationID = null;
                    if (lookup.trsRegistrationsLookup) {
                        const matchingRegistration = Object.values(
                            lookup.trsRegistrationsLookup,
                        ).find(
                            (r) =>
                                r.extension &&
                                r.extension.trim().toLowerCase() ===
                                    row.trsRegistration.trim().toLowerCase() &&
                                r.registrarName &&
                                r.registrarName.trim().toLowerCase() ===
                                    row.registrarName.trim().toLowerCase(),
                        );

                        if (matchingRegistration) {
                            registrationID = matchingRegistration.id;
                        }
                    }

                    // If we couldn't find the registration in the lookup, it doesn't exist
                    if (!registrationID) {
                        invalidTRSRegistration.push(row);
                        continue;
                    }

                    // Validation 1: Confirm registration and registrar exist using /TRSRegistration/{registrationID}
                    try {
                        await axios.get(`/TRSRegistration/${registrationID}`);
                    } catch (e) {
                        // Registration doesn't exist or isn't accessible
                        invalidTRSRegistration.push(row);
                        continue;
                    }

                    // Validation 2: Check if the number is available using /TRSRegistrations/Available/{companyID}
                    try {
                        await axios.get(
                            `/TRSRegistrations/Available/${companyID}`,
                        );
                        // API returns success - number is available
                    } catch (e) {
                        // API returns error - check if it's the same registration (update scenario)
                        if (
                            currentNumber &&
                            currentNumber.registrationID === registrationID
                        ) {
                            // Same registration - this is valid (updating same number)
                            continue;
                        }
                        // Number is assigned to a different registration
                        invalidTRSNumberUnavailable.push(row);
                        continue;
                    }
                } catch (e) {
                    console.error('Error validating TRS registration:', e);
                    invalidTRSRegistration.push(row);
                }
            }
        }

        return {
            invalidTRSIncomplete,
            invalidTRSRegistration,
            invalidTRSRegistrar,
            invalidTRSNumberUnavailable,
        };
    };

    const flagDataWithErrors = ({
        noValidationNumbersDNE = [],
        noValidationNumbersNotOK = [],
        validationNumbersDNE = [],
        validationNumbersNotOK = [],
        ocTrunksHaveInvalidCP = [],
        ocTrunksHaveInvalidTNU = [],
        trunksNotExist = [],
        invalidAddress = [],
        invalidOutboundCallerID = [],
        invalidUserPlan = [],
        invalidPrefix = [],
        invalidNumberAllocated = [],
        invalidPlanPermissions = [],
        invalidTRSIncomplete = [],
        invalidTRSRegistration = [],
        invalidTRSRegistrar = [],
        invalidTRSNumberUnavailable = [],
        data = [],
    }) => {
        NUMBERS_DNE_ERROR;
        NUMBER_NOT_OK_ERROR;
        NO_DATA_FOUND_ERROR;
        INVALID_CP_ERROR;
        INVALID_TNU_ERROR;
        TRUNKS_DNE_ERROR;
        ADDRESS_DNE_ERROR;
        OUTBOUND_DNE_ERROR;
        INVALID_USER_PLAN_ERROR;
        INVALID_PREFIX_ERROR;
        INVALID_NUMBER_ALLOCATED_ERROR;
        INVALID_PLAN_PERMISSIONS_ERROR;
        INVALID_TRS_REGISTRATION_ERROR;
        INVALID_TRS_REGISTRAR_ERROR;
        INVALID_TRS_NUMBER_UNAVAILABLE_ERROR;
        INVALID_TRS_INCOMPLETE_ERROR;
        let errors = concat(
            noValidationNumbersDNE.map((v) => ({
                ...v,
                error: NUMBERS_DNE_ERROR,
            })),
            noValidationNumbersNotOK.map((v) => ({
                ...v,
                error: NUMBER_NOT_OK_ERROR,
            })),
            validationNumbersDNE.map((v) => ({
                ...v,
                error: NUMBERS_DNE_ERROR,
            })),
            validationNumbersNotOK.map((v) => ({
                ...v,
                error: NUMBER_NOT_OK_ERROR,
            })),
            ocTrunksHaveInvalidCP.map((v) => ({
                ...v,
                error: INVALID_CP_ERROR,
            })),
            ocTrunksHaveInvalidTNU.map((v) => ({
                ...v,
                error: INVALID_TNU_ERROR,
            })),
            trunksNotExist.map((v) => ({ ...v, error: TRUNKS_DNE_ERROR })),
            invalidUserPlan.map((v) => ({
                ...v,
                error: INVALID_USER_PLAN_ERROR,
            })),
            invalidPrefix.map((v) => ({
                ...v,
                error: INVALID_PREFIX_ERROR,
            })),
            invalidNumberAllocated.map((v) => ({
                ...v,
                error: INVALID_NUMBER_ALLOCATED_ERROR,
            })),
            invalidPlanPermissions.map((v) => ({
                ...v,
                error: INVALID_PLAN_PERMISSIONS_ERROR,
            })),
            invalidTRSIncomplete.map((v) => ({
                ...v,
                error: INVALID_TRS_INCOMPLETE_ERROR,
            })),
            invalidTRSRegistration.map((v) => ({
                ...v,
                error: INVALID_TRS_REGISTRATION_ERROR,
            })),
            invalidTRSRegistrar.map((v) => ({
                ...v,
                error: INVALID_TRS_REGISTRAR_ERROR,
            })),
            invalidTRSNumberUnavailable.map((v) => ({
                ...v,
                error: INVALID_TRS_NUMBER_UNAVAILABLE_ERROR,
            })),
        );

        if (Cookies.get('selectedPlatformName') === 'Numbers') {
            errors = concat(
                errors,
                invalidAddress.map((v) => ({
                    ...v,
                    error: ADDRESS_DNE_ERROR, // or ADDRESS_WRONG_ERROR, depending on your logic
                })),
                invalidOutboundCallerID.map((v) => ({
                    ...v,
                    error: OUTBOUND_DNE_ERROR,
                })),
            );
        }

        const dataJSONTmp = {};
        // Build a lookup using numberID as key
        errors.forEach((data) => {
            const result =
                Cookies.get('selectedPlatformName') === 'Numbers'
                    ? data.id
                    : data.numberID;
            const id = result;
            (dataJSONTmp[id] || (dataJSONTmp[id] = [])).push(data.error);
        });
        // Map errors to each number in data by numberID
        const newDataWithErrors = data.map((item) => ({
            ...item,
            errors:
                dataJSONTmp[
                    Cookies.get('selectedPlatformName') === 'Numbers'
                        ? item.id
                        : item.numberID
                ] || [],
        }));
        return { newDataWithErrors };
    };
    // const processCSVData = async () => {
    //     changeVerificationProgresses(2);
    //     const convertedData = convertCSVHeaders(uploadData);
    //     console.log(convertedData)
    //     changeVerificationProgresses(2);
    //     // function processPlatformPriorities(data, tableResults, platformIds) {
    //     //     const passed = tableResults.passed.data;
    //     //     const failed = tableResults.failed.data;

    //     //     if (!Array.isArray(data)) {
    //     //         console.error("Input data is not an array.");
    //     //         return;
    //     //     }

    //     //     data.forEach(item => {
    //     //         const telephoneNumber = item.telephoneNumber;
    //     //         const numberId = item.id;

    //     //         if (numberId === undefined) {
    //     //             // id undefined, push to failed list and skip further processing
    //     //             failed.push({  telephoneNumber, errors: ['platform/number does not exist'] });
    //     //             return; // Skip to next item
    //     //         }

    //     //         if (typeof telephoneNumber === 'string' && telephoneNumber.includes(',')) {
    //     //             const [extractedNumber, platformsString] = telephoneNumber.split(',');

    //     //             if (platformsString) {
    //     //                 const platforms = platformsString.split(';');
    //     //                 const platformMap = {
    //     //                     Teams: platformIds.teams,
    //     //                     Zoom: platformIds.zoom,
    //     //                     SBCaaS: platformIds.sbcass,
    //     //                 };

    //     //                 const platformOrder = platforms.map(platform => platform.trim());
    //     //                 const defaultPlatforms = Object.keys(platformMap).filter(platform => !platformOrder.includes(platform));

    //     //                 platformOrder.forEach((platform, index) => {
    //     //                     let priority;
    //     //                     let platformId;

    //     //                     if (platformMap[platform]) {
    //     //                         priority = (index + 1) * 10;
    //     //                         platformId = platformMap[platform];
    //     //                     } else {
    //     //                         priority = (index + 1) * 10;
    //     //                         platformId = undefined;
    //     //                     }

    //     //                     const newRow = { numberID: numberId, priority, telephoneNumber: extractedNumber, platformId };
    //     //                     passed.push(newRow);
    //     //                 });

    //     //                 defaultPlatforms.forEach((defaultPlatform, index) => {
    //     //                     const priority = (platformOrder.length + index + 1) * 10;
    //     //                     const platformId = platformMap[defaultPlatform];
    //     //                     const newRow = {numberID: numberId, priority, telephoneNumber: extractedNumber, platformId };
    //     //                     passed.push(newRow);
    //     //                 });
    //     //             } else {
    //     //                 // No platforms string, default priority 10
    //     //                 const newRow = { numberID: numberId, priority: 10, telephoneNumber: extractedNumber, platformId: platformIds.teams };
    //     //                 passed.push(newRow);
    //     //                 const defaultPlatforms = Object.keys(platformIds).filter(platform => platform !== 'teams');
    //     //                 defaultPlatforms.forEach((platform, index) => {
    //     //                     const priority = (index + 2) * 10;
    //     //                     const newRow = { numberID: numberId, priority, telephoneNumber: extractedNumber, platformId: platformIds[platform] };
    //     //                     passed.push(newRow);
    //     //                 });
    //     //             }
    //     //         } else {
    //     //             // No comma, default priority 10
    //     //             const newRow = { numberID: numberId, priority: 10, telephoneNumber, platformId: platformIds.teams };
    //     //             passed.push(newRow);
    //     //             const defaultPlatforms = Object.keys(platformIds).filter(platform => platform !== 'teams');
    //     //             defaultPlatforms.forEach((platform, index) => {
    //     //                 const priority = (index + 2) * 10;
    //     //                 const newRow = {numberID: numberId, priority, telephoneNumber, platformId: platformIds[platform] };
    //     //                 passed.push(newRow);
    //     //             });
    //     //         }
    //     //     });
    //     // }
    //     async function processPlatformPriorities(data, tableResults, platformIds) {
    //         const passed = tableResults.passed.data;
    //         const failed = tableResults.failed.data;

    //         if (!Array.isArray(data)) {
    //             console.error("Input data is not an array.");
    //             return;
    //         }

    //         for (const item of data) {
    //             const telephoneNumber = item.telephoneNumber;
    //             const numberId = item.id;

    //             if (numberId === undefined) {
    //                 failed.push({ telephoneNumber, errors: ['platform/number does not exist'] });
    //                 continue;
    //             }

    //             let extractedNumber = telephoneNumber;
    //             let platformsString = null;

    //             if (typeof telephoneNumber === 'string' && telephoneNumber.includes(',')) {
    //                 [extractedNumber, platformsString] = telephoneNumber.split(',');
    //             }

    //             let existingPlatformNumbersData = [];
    //             try {
    //                 const existingPlatformNumbers = await axios.get(`/api/ui/PlatformNumbers/${numberId}`);
    //                 existingPlatformNumbersData = existingPlatformNumbers.data || [];
    //             } catch (error) {
    //                 console.error(`API call failed for NumberID ${numberId}:`, error);
    //                 failed.push({ telephoneNumber, errors: [`Failed to fetch existing platform data: ${error.message}`] });
    //                 continue;
    //             }

    //             const processedPlatformIds = new Set();

    //             if (platformsString) {
    //                 const platforms = platformsString.split(';').map(p => p.trim());
    //                 const platformOrder = platforms.filter(p => platformIds[p]);

    //                 platformOrder.forEach((platform, index) => {
    //                     const priority = (index + 1) * 10;
    //                     const platformId = platformIds[platform];
    //                     if (platformId && !processedPlatformIds.has(platformId)) {
    //                         const existingInfo = existingPlatformNumbersData.find(e => e.platformId === platformId) || {};
    //                         const newRow = {
    //                             ...existingInfo, // Carry all information from the API response
    //                             priority: priority,
    //                             platformId: platformId, // Ensure platformId is set
    //                         };
    //                         passed.push(newRow);
    //                         processedPlatformIds.add(platformId);
    //                     }
    //                 });

    //                 // Add default platforms that weren't explicitly ordered
    //                 Object.keys(platformIds).forEach(platformKey => {
    //                     const platformId = platformIds[platformKey];
    //                     if (!processedPlatformIds.has(platformId)) {
    //                         const priority = (platformOrder.length + [...processedPlatformIds].length + 1) * 10;
    //                         const existingInfo = existingPlatformNumbersData.find(e => e.platformId === platformId) || {};
    //                         const newRow = {
    //                             ...existingInfo, // Carry all information from the API response
    //                             priority: priority,
    //                             platformId: platformId, // Ensure platformId is set
    //                         };
    //                         passed.push(newRow);
    //                         processedPlatformIds.add(platformId);
    //                     }
    //                 });
    //             } else {
    //                 // No comma, default priority 10 for the first platform
    //                 const firstPlatformKey = Object.keys(platformIds)[0];
    //                 if (firstPlatformKey) {
    //                     const platformId = platformIds[firstPlatformKey];
    //                     const existingInfo = existingPlatformNumbersData.find(e => e.platformId === platformId) || {};
    //                     const newRow = { ...existingInfo, priority: 10, platformId: platformId };
    //                     passed.push(newRow);
    //                     processedPlatformIds.add(platformId);

    //                     // Add other platforms with incrementing priority
    //                     Object.keys(platformIds).slice(1).forEach((platformKey, index) => {
    //                         const priority = (index + 2) * 10;
    //                         const platformId = platformIds[platformKey];
    //                         const existingInfo = existingPlatformNumbersData.find(e => e.platformId === platformId) || {};
    //                         const newRow = { ...existingInfo, priority: priority, platformId: platformId };
    //                         passed.push(newRow);
    //                         processedPlatformIds.add(platformId);
    //                     });
    //                 }
    //             }
    //         }
    //     }
    //     const tableResults = {
    //         passed: { data: [] },
    //         failed: { data: [] },
    //     };

    //     const platformIds = {
    //         teams: 'fac64979-385b-42ce-8b1c-d30e87822304',
    //         zoom: '46140cdd-5134-4088-b474-9aad13987c9f',
    //         sbcass: '5873b368-64b4-4262-829a-a626cef2f4f6',
    //     };
    //     if(Cookies.get('selectedPlatformName') === 'Priority'){
    //     processPlatformPriorities(convertedData, tableResults, platformIds);
    //     changeVerificationProgresses(2);
    //     changeVerificationChecks(2);
    //     changeVerificationProgresses(3);
    //     changeVerificationChecks(3);
    //     changeVerificationProgresses(3);
    //     changeVerificationChecks(3);
    //     console.log(tableResults.passed.data);
    //     setVerificationResult(tableResults);
    //     setPayloads(tableResults.passed.data.map(item => {
    //         const { tableData, ...rest } = item; // Use object destructuring to exclude tableData
    //         return rest; // Return the new object without tableData
    //       }));
    //     return
    //     }
    //     if(Cookies.get('selectedPlatformName') !== 'Priority'){
    //         const { noNeedValidation, needValidation, unchanged } =
    //         filterChangedData(convertedData);
    //         changeVerificationProgresses(2);
    //         changeVerificationChecks(2);
    //     if (noNeedValidation.length < 1 && needValidation.length < 1) {
    //         setError(NO_DATA_FOUND_ERROR);
    //     }
    //     console.log(needValidation)
    //     const {
    //         numbersNotExist: noValidationNumbersDNE,
    //         numbersNotOK: noValidationNumbersNotOK,
    //         numbersOKAndExist: noValidationNumbersOKAndExist,
    //     } = verifyNumbers(noNeedValidation);
    //     changeVerificationProgresses(3);
    //     const {
    //         numbersNotExist: validationNumbersDNE,
    //         numbersNotOK: validationNumbersNotOK,
    //         numbersOKAndExist: validationNumbersOKAndExist,
    //     } = verifyNumbers(needValidation);
    //     changeVerificationProgresses(3);
    //     changeVerificationChecks(3);
    //      console.log(validationNumbersDNE)
    //      console.log(validationNumbersNotOK)
    //      console.log(validationNumbersOKAndExist)
    //     let verificationResults = {
    //         ocTrunksHaveInvalidCP: [],
    //         ocTrunksHaveInvalidTNU: [],
    //         trunksNotExist: [],
    //         invalidAddress: [],
    //         invalidOutboundCallerID: [],
    //     };

    //     if (Cookies.get('selectedPlatformName') !== 'numbers') {
    //         verificationResults = verifyTrunks(validationNumbersOKAndExist);
    //     } else {
    //         console.log('here')
    //         verificationResults.invalidAddress = verifyAddress(validationNumbersOKAndExist).invalidAddress;
    //         changeVerificationProgresses(4);
    //         changeVerificationChecks(4);
    //         verificationResults.invalidOutboundCallerID = verifyOutboundCallerID(validationNumbersOKAndExist).invalidOutboundCallerID;
    //         changeVerificationProgresses(5);
    //         changeVerificationChecks(5);
    //     }

    //     const { newDataWithErrors: noNeedValidationWithErrors } =
    //         flagDataWithErrors({
    //             noValidationNumbersDNE,
    //             noValidationNumbersNotOK,
    //             data: noNeedValidation,
    //         });
    //     const { newDataWithErrors: needValidationWithErrors } =
    //         flagDataWithErrors({
    //             validationNumbersDNE,
    //             validationNumbersNotOK,
    //             ...verificationResults,
    //             data: needValidation,
    //         });
    //     const { allSucceedData } = reportResult({
    //         noNeedValidationWithErrors,
    //         needValidationWithErrors,
    //         unchanged,
    //     });

    //     transformDataToPayload({
    //         data: allSucceedData,
    //         noValidationNumbersOKAndExist,
    //     });
    // }
    // };
    const processCSVData = async () => {
        // Guard against the effect firing before a file has been uploaded
        // (e.g. on initial mount, when `lookup` is still its default state) —
        // convertCSVHeaders requires a real array to work with.
        if (!uploadData) return;
        changeVerificationProgresses(2);
        const convertedData = convertCSVHeaders(uploadData);
        changeVerificationProgresses(2);

        const tableResults = {
            passed: { data: [] },
            failed: { data: [] },
        };

        async function processPlatformPriorities(
            data,
            tableResults,
            platformIds,
            availablePlatforms,
        ) {
            const passed = tableResults.passed.data;
            const failed = tableResults.failed.data;

            if (!Array.isArray(data)) {
                console.error('Input data is not an array.');
                return;
            }

            for (const item of data) {
                const telephoneNumber = item.telephoneNumber;
                const platformPriorities = item.platformPriorities || {}; // Platform name -> priority value mapping from CSV
                const currentPlatformPriorities =
                    item.currentPlatformPriorities || {}; // Current platform assignments

                if (!telephoneNumber) {
                    failed.push({
                        telephoneNumber,
                        platform: '',
                        errors: ['Telephone number is required'],
                    });
                    continue;
                }

                // Find the number in the lookup — unknown numbers are skipped (noted, not hard-failed)
                const numberInfo = lookup.presentableNumbersLookup.find(
                    (n) => n.telephoneNumber === String(telephoneNumber),
                );
                if (!numberInfo) {
                    // Invalid / non-existent numbers are ignored per #2547
                    continue;
                }

                // Build a map of platform name -> platform ID for validation
                const platformNameToId = {};
                availablePlatforms.forEach((platform) => {
                    platformNameToId[platform.name] = platform.id;
                });

                // Check for removed platforms (platforms that exist in current but are empty in CSV)
                const removedPlatforms = [];
                Object.keys(currentPlatformPriorities).forEach(
                    (platformName) => {
                        const currentPriority =
                            currentPlatformPriorities[platformName];
                        const csvPriority = platformPriorities[platformName];

                        // Platform had a priority before but is now empty in CSV = removal attempt
                        const hadPriority =
                            currentPriority !== '' &&
                            currentPriority !== null &&
                            currentPriority !== undefined;
                        const csvIsEmpty =
                            csvPriority === '' ||
                            csvPriority === null ||
                            csvPriority === undefined;

                        if (hadPriority && csvIsEmpty) {
                            removedPlatforms.push(platformName);
                        }
                    },
                );

                // If any platforms are being removed, add to failed list
                if (removedPlatforms.length > 0) {
                    const platformNames = removedPlatforms
                        .map((name) => (name === 'Eightbyeight' ? '8x8' : name))
                        .join(', ');
                    failed.push({
                        telephoneNumber,
                        platform: platformNames,
                        errors: [
                            `Cannot remove platform assignments. Platforms being removed: ${platformNames}. To remove a platform assignment, please use the platform-specific management page.`,
                        ],
                    });
                    continue;
                }

                // Validate that all platform names in CSV match available platforms
                const csvPlatformNames = Object.keys(platformPriorities);
                let hasInvalidPlatform = false;
                for (const platformName of csvPlatformNames) {
                    if (!platformNameToId[platformName]) {
                        const displayName =
                            platformName === 'Eightbyeight'
                                ? '8x8'
                                : platformName;
                        failed.push({
                            telephoneNumber,
                            platform: displayName,
                            errors: [
                                `Unrecognized platform: ${displayName}. Available platforms: ${Object.keys(platformNameToId).join(', ')}`,
                            ],
                        });
                        hasInvalidPlatform = true;
                        break;
                    }
                }

                if (hasInvalidPlatform) {
                    continue;
                }

                // Fetch existing platform numbers
                let existingPlatformNumbersData = [];
                try {
                    const existingPlatformNumbers = await axios.get(
                        `/PlatformNumbers/${numberInfo.id}`,
                    );
                    existingPlatformNumbersData =
                        existingPlatformNumbers.data || [];
                } catch (error) {
                    failed.push({
                        telephoneNumber,
                        platform: '',
                        errors: [
                            `Failed to fetch existing platform data: ${error.message}`,
                        ],
                    });
                    continue;
                }

                const processedPlatformIds = new Set();

                // Process platforms from CSV with their priorities (only those with values).
                // A number qualifies for changedData if ANY of its platform
                // priorities differ, so each platform here must be checked
                // individually against its existing value — otherwise every
                // platform on the row gets resubmitted even when only one
                // of them actually changed.
                Object.entries(platformPriorities).forEach(
                    ([platformName, priority]) => {
                        // Skip if priority is empty
                        if (
                            priority === '' ||
                            priority === null ||
                            priority === undefined
                        ) {
                            return;
                        }

                        const targetPlatformId = platformNameToId[platformName];

                        if (
                            targetPlatformId &&
                            !processedPlatformIds.has(targetPlatformId)
                        ) {
                            const existingInfo =
                                existingPlatformNumbersData.find(
                                    (e) => e.platformID === targetPlatformId,
                                );
                            const existingPriority =
                                existingInfo?.priority === undefined ||
                                existingInfo?.priority === null
                                    ? ''
                                    : String(existingInfo.priority);
                            const hasRealChange =
                                String(priority) !== existingPriority;

                            if (hasRealChange) {
                                const displayName =
                                    platformName === 'Eightbyeight'
                                        ? '8x8'
                                        : platformName;
                                const newRow = existingInfo
                                    ? {
                                          ...existingInfo,
                                          priority: priority,
                                          platform: displayName,
                                      }
                                    : {
                                          numberID: numberInfo.id,
                                          telephoneNumber: telephoneNumber,
                                          platformID: targetPlatformId,
                                          priority: priority,
                                          platform: displayName,
                                      };
                                passed.push(newRow);
                            }
                            processedPlatformIds.add(targetPlatformId);
                        }
                    },
                );

                // Platforms not mentioned in the CSV row (or left empty) are
                // genuinely unchanged — they must not be resubmitted.
            }
        }
        async function fetchAndConvertPlatformIds() {
            try {
                const platformAvailableResponse = await axios.get(
                    `/Company/${companyID}/Platforms/Available`,
                );
                const platformAvailableData = platformAvailableResponse.data;
                const platformIds = {};

                platformAvailableData.forEach((platform) => {
                    let lowercaseName = platform.name.toLowerCase();

                    if (lowercaseName === 'microsoft_teams') {
                        lowercaseName = 'teams';
                    } else if (lowercaseName === 'microsoft_acs') {
                        lowercaseName = 'acs';
                    }

                    platformIds[lowercaseName] = platform.id;
                });

                return platformIds;
            } catch (error) {
                return {}; // Return an empty object in case of an error
            }
        }
        if (Cookies.get('selectedPlatformName') === 'Priority') {
            const availablePlatformsResponse = await axios.get(
                `/Company/${companyID}/Platforms/Available`,
            );
            // Filter unique platforms based on id
            const platforms = availablePlatformsResponse.data.reduce(
                (unique, platform) => {
                    const isDuplicate = unique.some(
                        (item) => item.id === platform.id,
                    );
                    if (
                        !isDuplicate &&
                        platform.id !== 'numbers' &&
                        platform.id !== 'priority'
                    ) {
                        unique.push(platform);
                    }
                    return unique;
                },
                [],
            );

            const promises = platforms.map((platform) =>
                axios.get(
                    `/company/${companyID}/PlatformNumbers/${platform.id}`,
                ),
            );

            const responses = await Promise.all(promises);

            // Build a map of telephone numbers with their current platform assignments
            const currentPlatformAssignments = responses.reduce(
                (acc, res, index) => {
                    const platformData = res.data.platformNumbers;
                    const platform = platforms[index];

                    platformData.forEach((number) => {
                        const key = number.telephoneNumber;
                        if (!acc.has(key)) {
                            acc.set(key, {
                                telephoneNumber: key,
                                numberID: number.numberID,
                                platformPriorities: {}, // Platform name -> priority mapping
                            });
                        }

                        const entry = acc.get(key);
                        // Store current priority for this platform (including empty ones)
                        entry.platformPriorities[platform.name] =
                            number.priority || '';
                    });
                    return acc;
                },
                new Map(),
            );

            // Compare CSV data with current assignments to find only changed numbers
            const changedData = [];

            convertedData.forEach((csvItem) => {
                const telephoneNumber = String(csvItem.telephoneNumber).trim();
                const currentAssignment =
                    currentPlatformAssignments.get(telephoneNumber);

                if (!currentAssignment) {
                    // New number not in system - skip or handle as needed
                    return;
                }

                let hasChanges = false;
                const csvPlatforms = csvItem.platformPriorities || {};

                // Check if any platform priority has changed
                platforms.forEach((platform) => {
                    const platformName = platform.name;
                    const csvPriority = csvPlatforms[platformName];
                    const currentPriority =
                        currentAssignment.platformPriorities[platformName];

                    // Convert to comparable format (treat undefined/null/empty as same)
                    const csvVal =
                        csvPriority === undefined ||
                        csvPriority === null ||
                        csvPriority === ''
                            ? ''
                            : String(csvPriority);
                    const currentVal =
                        currentPriority === undefined ||
                        currentPriority === null ||
                        currentPriority === ''
                            ? ''
                            : String(currentPriority);

                    if (csvVal !== currentVal) {
                        hasChanges = true;
                    }
                });

                if (hasChanges) {
                    changedData.push({
                        ...csvItem,
                        currentPlatformPriorities:
                            currentAssignment.platformPriorities, // Include for validation
                    });
                }
            });

            //const platformIds = await fetchAndConvertPlatformIds();

            // Get available platforms for validation
            const availablePlatformsForValidation =
                availablePlatformsResponse.data.reduce((unique, platform) => {
                    const isDuplicate = unique.some(
                        (item) => item.id === platform.id,
                    );
                    if (
                        !isDuplicate &&
                        platform.id !== 'numbers' &&
                        platform.id !== 'priority'
                    ) {
                        unique.push(platform);
                    }
                    return unique;
                }, []);

            const platformIds = await fetchAndConvertPlatformIds();
            await processPlatformPriorities(
                changedData,
                tableResults,
                platformIds,
                availablePlatformsForValidation,
            ); // Call with await and available platforms
            changeVerificationProgresses(2);
            changeVerificationChecks(2);
            changeVerificationProgresses(3);
            changeVerificationChecks(3);
            changeVerificationProgresses(3);
            changeVerificationChecks(3);
            setVerificationResult(tableResults);
            // Remove display-only properties (tableData and platform) before creating payloads
            setPayloads(
                tableResults.passed.data.map((item) => {
                    const { tableData, platform, ...rest } = item;
                    return rest;
                }),
            );
            return;
        }

        if (Cookies.get('selectedPlatformName') !== 'Priority') {
            const { noNeedValidation, needValidation, unchanged } =
                filterChangedData(convertedData);
            changeVerificationProgresses(2);
            changeVerificationChecks(2);
            if (noNeedValidation.length < 1 && needValidation.length < 1) {
                setError(NO_DATA_FOUND_ERROR);
            }
            const {
                numbersNotExist: noValidationNumbersDNE,
                numbersNotOK: noValidationNumbersNotOK,
                numbersOKAndExist: noValidationNumbersOKAndExist,
            } = verifyNumbers(noNeedValidation);
            changeVerificationProgresses(3);
            const {
                numbersNotExist: validationNumbersDNE,
                numbersNotOK: validationNumbersNotOK,
                numbersOKAndExist: validationNumbersOKAndExist,
            } = verifyNumbers(needValidation);
            changeVerificationProgresses(3);
            changeVerificationChecks(3);
            let verificationResults = {
                ocTrunksHaveInvalidCP: [],
                ocTrunksHaveInvalidTNU: [],
                invalidAddress: [],
                invalidOutboundCallerID: [],
                invalidTRSIncomplete: [],
                invalidTRSRegistration: [],
                invalidTRSRegistrar: [],
                invalidTRSNumberUnavailable: [],
            };
            if (Cookies.get('selectedPlatformName') === '8x8') {
                verificationResults = verifyEightByEight(
                    validationNumbersOKAndExist,
                );
            } else if (Cookies.get('selectedPlatformName') !== 'Numbers') {
                verificationResults = verifyTrunks(validationNumbersOKAndExist);
            } else {
                verificationResults.invalidAddress = verifyAddress(
                    validationNumbersOKAndExist,
                ).invalidAddress;
                changeVerificationProgresses(4);
                changeVerificationChecks(4);
                verificationResults.invalidOutboundCallerID =
                    verifyOutboundCallerID(
                        validationNumbersOKAndExist,
                    ).invalidOutboundCallerID;
                verificationResults = verifyPlans(
                    validationNumbersOKAndExist,
                    lookup.availablePlansLookup,
                );
                changeVerificationProgresses(5);
                changeVerificationChecks(5);

                // Verify TRS registration for Numbers platform only - but only for numbers where TRS fields changed
                const numbersWithTRSChanges =
                    validationNumbersOKAndExist.filter((row) => {
                        const currentNumber =
                            lookup.currentNumbersLookup[row.id];
                        if (!currentNumber) return true; // New number, validate

                        // Get current TRS data
                        let currentTrsRegistration = '';
                        let currentTrsRegistrar = '';
                        if (
                            currentNumber.registrationID &&
                            lookup.trsRegistrationsLookup &&
                            lookup.trsRegistrationsLookup[
                                currentNumber.registrationID
                            ]
                        ) {
                            const trsData =
                                lookup.trsRegistrationsLookup[
                                    currentNumber.registrationID
                                ];
                            currentTrsRegistration = (trsData.extension || '')
                                .trim()
                                .toLowerCase();
                            currentTrsRegistrar = (trsData.registrarName || '')
                                .trim()
                                .toLowerCase();
                        }

                        // Get new TRS data
                        const newTrsRegistration = (row.trsRegistration || '')
                            .trim()
                            .toLowerCase();
                        const newTrsRegistrar = (row.registrarName || '')
                            .trim()
                            .toLowerCase();

                        // Only validate if TRS data has changed
                        return (
                            currentTrsRegistration !== newTrsRegistration ||
                            currentTrsRegistrar !== newTrsRegistrar
                        );
                    });

                changeVerificationProgresses(6); // Validating registrar
                changeVerificationChecks(6);
                const trsValidation = await verifyTRSRegistration(
                    numbersWithTRSChanges,
                );
                changeVerificationProgresses(7); // Validating registration name
                changeVerificationChecks(7);
                verificationResults = {
                    ...verificationResults,
                    ...trsValidation,
                };
            }

            const { newDataWithErrors: noNeedValidationWithErrors } =
                flagDataWithErrors({
                    noValidationNumbersDNE,
                    noValidationNumbersNotOK,
                    data: noNeedValidation,
                });
            const { newDataWithErrors: needValidationWithErrors } =
                flagDataWithErrors({
                    validationNumbersDNE,
                    validationNumbersNotOK,
                    ...verificationResults,
                    data: needValidation,
                });
            const { allSucceedData } = reportResult({
                noNeedValidationWithErrors,
                needValidationWithErrors,
                unchanged,
            });

            transformDataToPayload({
                data: allSucceedData,
                noValidationNumbersOKAndExist,
            });
        }
    };
    const transformDataToPayload = ({
        data,
        noValidationNumbersOKAndExist,
    }) => {
        const platformName = Cookies.get('selectedPlatformName');
        const idKey = platformName === 'Numbers' ? 'id' : 'numberID';

        const newPayload = data.map((v) => {
            const apiData = lookup.currentNumbersLookup[v[idKey]];
            const plans = lookup.availablePlansLookup[v.plan] || [];
            const numbersData = {
                ...apiData,
                ...v,
                planID: plans.id,
            };
            const doesNotNeedLookup = noValidationNumbersOKAndExist.find(
                (number) => number[idKey] === v[idKey],
            );
            if (doesNotNeedLookup) {
                return numbersData;
            }

            let trunkID = null;
            let sipHeader = null;
            let trunkType = null;
            let operatorConnectCallingProfileID = null;
            let registrationID = apiData?.registrationID || null;

            if (platformName !== 'Numbers') {
                const trunkObject = lookup.trunksLookup[v.trunk] || {};
                trunkID = trunkObject?.id;
                trunkType = trunkObject?.trunkType?.name;
                sipHeader = v.trunk;
                const isOCTrunk = trunkType === 'MS Operator Connect';
                operatorConnectCallingProfileID = isOCTrunk
                    ? (trunkObject?.callingProfileList ?? []).find(
                          (cp) => cp.name === v.operatorConnectCallingProfile,
                      )?.id
                    : v.operatorConnectCallingProfileID;
            }

            if (platformName === 'Numbers') {
                // Handle TRS Registration ID
                const hasRegistration =
                    v.trsRegistration && v.trsRegistration.trim() !== '';
                const hasRegistrar =
                    v.registrarName && v.registrarName.trim() !== '';

                if (!hasRegistration && !hasRegistrar) {
                    // Both empty - set registrationID to empty string to clear it
                    registrationID = '';
                } else if (
                    hasRegistration &&
                    hasRegistrar &&
                    lookup.trsRegistrationsLookup
                ) {
                    // Both provided - find the registrationID from the lookup
                    const registration = Object.values(
                        lookup.trsRegistrationsLookup,
                    ).find(
                        (r) =>
                            r.extension &&
                            r.extension.trim() === v.trsRegistration.trim() &&
                            r.registrarName &&
                            r.registrarName.trim() === v.registrarName.trim(),
                    );
                    if (registration) {
                        registrationID = registration.id;
                    }
                }
            }

            if (platformName !== 'Numbers') {
                return {
                    ...numbersData,
                    trunkID,
                    sipHeader,
                    trunkType,
                    operatorConnectCallingProfileID,
                };
            } else {
                return {
                    ...numbersData,
                    registrationID,
                };
            }
        });
        setPayloads(newPayload);
    };

    /**
     * once lookups are available, process our uploaded CSV.
     */

    useEffect(() => {
        processCSVData();
    }, [lookup]);

    return {
        handleDownloadCSVTemplate,
        handleMock,
        handleUploadData,
        templateLoading,
        verificationChecks,
        uploadData,
        verificationResult,
        payloads,
        handleExportInvalidData,
        handleExportModifiedData,
        handleExportUnchangedData,
        error,
        csvLimit,
    };
}
